import React, { useCallback, useEffect, useRef, useState, forwardRef } from 'react';
import { styled } from '@compiled/react';
import {
	NudgeSpotlight,
	type NudgeType,
	type NudgeSpotlightRef,
} from '@atlassiansox/nudge-tooltip';
import { SpotlightPulse } from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	fireTrackAnalytics,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';
import { FireScreenAnalytics } from '@atlassian/jira-product-analytics-bridge/src/utils/fire-analytics-component';
import messages from './messages.tsx';

interface Props {
	children: React.ReactElement;
	isFocussed: boolean;
	markAsSeen: () => void;
	stop?: () => void;
}

export const MESSAGE_ID = 'jsw-pp-end-user-activated-confluence_v2';

const StyledNudge: NudgeType = forwardRef(({ children, nudgeTestId, ...restProps }, ref) => (
	<NudgeWrapper {...restProps} ref={ref} data-testid={nudgeTestId}>
		{children}
	</NudgeWrapper>
));

export const EndUserActivateNudge = ({ children, isFocussed, stop, markAsSeen }: Props) => {
	const { formatMessage } = useIntl();
	const [dismissed, setDismissed] = useState(false);
	const spotlightRef = useRef<NudgeSpotlightRef>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// show the spotlight when focussed (via keyboard)
	useEffect(() => {
		if (isFocussed) {
			spotlightRef.current?.toggleCardVisibility?.(true);
		}
	}, [isFocussed, spotlightRef]);

	const hideMessage = useCallback(() => {
		setDismissed(true);
		stop?.();
		fireTrackAnalytics(createAnalyticsEvent({}), 'endUserActivateProjectPagesSpotlight dismissed', {
			messageId: MESSAGE_ID,
		});
	}, [createAnalyticsEvent, stop]);

	const handleOkClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({}),
			'button clicked',
			'endUserActivateProjectPagesSpotlightDismissButton',
			{
				messageId: MESSAGE_ID,
			},
		);
		hideMessage();
	}, [createAnalyticsEvent, hideMessage]);

	return (
		<ContextualAnalyticsData
			sourceName="endUserActivateProjectPagesSpotlightNudge"
			sourceType="Screen"
		>
			<FireScreenAnalytics attributes={{ messageId: MESSAGE_ID }} />
			<NudgeSpotlight
				ref={spotlightRef}
				hidden={dismissed}
				setHidden={hideMessage}
				content={
					<ContextualAnalyticsData
						sourceName="endUserActivateProjectPagesSpotlight"
						sourceType="Screen"
					>
						<FireScreenAnalytics attributes={{ messageId: MESSAGE_ID }} />
						{formatMessage(messages.content)}
					</ContextualAnalyticsData>
				}
				actions={[
					{
						text: formatMessage(messages.primaryActionText),
						onClick: handleOkClick,
					},
				]}
				position="right-end"
				onOutsideClick={hideMessage}
				nudgeTestId={`${MESSAGE_ID}-nudge`}
				cardTestId={`${MESSAGE_ID}-card`}
				showSpotlightCardOnFirstRender={false}
				onShow={markAsSeen}
				nudge={StyledNudge}
			>
				<SpotlightPulse pulse={!dismissed} radius={3} testId={`${MESSAGE_ID}-nudge-pulsing-border`}>
					{children}
				</SpotlightPulse>
			</NudgeSpotlight>
		</ContextualAnalyticsData>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NudgeWrapper = styled.div({
	marginBottom: `${token('space.025')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div > a > span': {
		paddingLeft: `${token('space.100')}`,
	},
});
